
import { supabase, enhancedQuery } from "@/integrations/supabase/client";
import { Organization } from "@/types/flight";

type OrganizationCreateInput = {
  name: string;
  code: string;
  is_active?: boolean;
};

type OrganizationUpdateInput = {
  name?: string;
  code?: string;
  is_active?: boolean;
};

// Get all organizations
export const getOrganizations = async (): Promise<Organization[]> => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching organizations:', error);
      throw error;
    }
    
    return data as Organization[];
  } catch (error) {
    console.error('Unexpected error in getOrganizations:', error);
    throw error;
  }
};

// Get a single organization by ID
export const getOrganizationById = async (id: string): Promise<Organization> => {
  return enhancedQuery(async () => {
    const result = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();
      
    return result;
  });
};

// Create a new organization
export const createOrganization = async (input: OrganizationCreateInput): Promise<Organization> => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .insert([
        {
          name: input.name,
          code: input.code,
          is_active: input.is_active !== undefined ? input.is_active : true,
        }
      ])
      .select();
    
    if (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('Failed to create organization: No data returned');
    }
    
    return data[0] as Organization;
  } catch (error) {
    console.error('Unexpected error in createOrganization:', error);
    throw error;
  }
};

// Update an existing organization
export const updateOrganization = async (id: string, input: OrganizationUpdateInput): Promise<Organization> => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .update({
        ...(input.name !== undefined && { name: input.name }),
        ...(input.code !== undefined && { code: input.code }),
        ...(input.is_active !== undefined && { is_active: input.is_active }),
      })
      .eq('id', id)
      .select();
    
    if (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('Failed to update organization: Organization not found');
    }
    
    return data[0] as Organization;
  } catch (error) {
    console.error('Unexpected error in updateOrganization:', error);
    throw error;
  }
};

// Delete an organization
export const deleteOrganization = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('organizations')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  } catch (error) {
    console.error('Unexpected error in deleteOrganization:', error);
    throw error;
  }
};
